import { ButtonPrimary, ButtonTertiary } from "@nova/component-btn"
import { Tag } from "@nova/ds-component-tag"
import { Input } from "@nova/ds-component-input"
import { TriCheckbox } from "@nova/component-checkbox"
import { Select } from "@nova/component-select"
import { DateFormat, formatDate } from "@nova/format"
import { config } from "@nova/configuration/browser"
import * as Icons from "@nova/component-icons"
import { GroupType, SatelliteSensorType, SatelliteTemporalBias, zIndex } from "@nova/constants"
import { useEffect, useRef, useState } from "react"
import classNames from "classnames"
import { v4 as randomUUID } from "uuid"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import store from "integrations/redux/store"
import { useViewport } from "features/map/GlobalViewState"
import { SidePanelPluginActionsNavigator } from "panels/content/side-panel/plugin-actions/SidePanelPluginActionsNavigator"
import { SidePanelPluginActionsRenamer } from "panels/content/side-panel/plugin-actions/SidePanelPluginActionsRenamer"
import { setActiveFeatureId, setActiveLayerId } from "panels/content/side-panel/slice.new"
import { setActiveSidePanelModal } from "panels/content/side-panel/slice"
import { selectUserID } from "user/profile/selectors"
import { createSatelliteFlight } from "features/pubsub/entityActions/flights/satellite"
import { createGroup } from "features/pubsub/entityActions/cubes/groups"
import { createNewLayerFractionalIndex } from "@nova/serializer/src/client/util/fractional_indexing"
import { SatelliteDateDirectionModal } from "./SatelliteDateDirectionModal"
import {
    MIN_ZOOM_TO_CONFIRM,
    RegionSelector,
    RegionSelectorHandle,
    ZOOM_IN_HINT_TEXT,
} from "./RegionSelector"

const sensorTypeOptions = [
    { value: SatelliteSensorType.Visual, label: "Visual" },
    { value: SatelliteSensorType.Infrared, label: "Infrared" },
    { value: SatelliteSensorType.Radar, label: "Radar" },
]

export const temporalBiasOptions: {
    value: SatelliteTemporalBias
    label: string
    Icon: Icons.IconComponent
}[] = [
    { value: "before", label: "Before", Icon: Icons.AlignHorizontalRight },
    { value: "after", label: "After", Icon: Icons.AlignHorizontalLeft },
    { value: "closest", label: "Closest to", Icon: Icons.AlignHorizontalCenter },
]

const sensorInfo: Record<
    SatelliteSensorType,
    {
        image: string
        label: string
        description: string
        resolution: string
        revisit: string
        dataAccess: string
        attribution: string
    }
> = {
    [SatelliteSensorType.Visual]: {
        image: "/images/plugins/satellite/satellite-sensor-visual.webp",
        label: "Visual",
        description: "True-color imagery for general situational awareness.",
        resolution: "10m",
        revisit: "3-4 day revisit",
        dataAccess: "Open Data",
        attribution: "Copernicus Sentinel-2",
    },
    [SatelliteSensorType.Infrared]: {
        image: "/images/plugins/satellite/satellite-sensor-infrared.webp",
        label: "Infrared",
        description: "Shortwave infrared, useful for detecting heat signatures and moisture.",
        resolution: "20m",
        revisit: "3-4 day revisit",
        dataAccess: "Open Data",
        attribution: "Copernicus Sentinel-2",
    },
    [SatelliteSensorType.Radar]: {
        image: "/images/plugins/satellite/satellite-sensor-radar.webp",
        label: "Radar",
        description: "All-weather radar imagery that sees through cloud and smoke.",
        resolution: "30m",
        revisit: "6 day revisit",
        dataAccess: "Open Data",
        attribution: "Copernicus Sentinel-1",
    },
}

// Earliest Sentinel-1/2 imagery could exist - Sentinel-1A launched 2014-04-03.
const EARLIEST_SATELLITE_DATE = new Date(2014, 0, 1)

// Use local calendar fields here, not toISOString()/`new Date(string)` (UTC-based) - or the date shifts a day in some timezones.
const toDateInputValue = (value: Date) => {
    const year = value.getFullYear()
    const month = String(value.getMonth() + 1).padStart(2, "0")
    const day = String(value.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
}

const parseDateInputValue = (value: string) => {
    const [year, month, day] = value.split("-").map(Number)
    return new Date(year, month - 1, day)
}

const startOfDay = (value: Date) => new Date(value.getFullYear(), value.getMonth(), value.getDate())

function OptionRow({
    control,
    Icon,
    label,
    isActive,
    isDisabled,
    onClick,
    onMouseEnter,
    onMouseLeave,
    className,
}: {
    control: React.ReactNode
    Icon?: Icons.IconComponent
    label: string
    isActive: boolean
    isDisabled?: boolean
    onClick: () => void
    onMouseEnter?: (e: React.MouseEvent<HTMLDivElement>) => void
    onMouseLeave?: () => void
    className?: string
}) {
    return (
        <div
            className={classNames(
                "flex items-center gap-[11px] -mx-2 px-2 py-1 rounded-md select-none hover:bg-white-200",
                isDisabled ? "cursor-not-allowed" : "cursor-pointer",
                className
            )}
            onClick={isDisabled ? undefined : onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {control}
            {Icon && (
                <Icons.SmallIcon
                    Icon={Icon}
                    iconClassName={isActive ? "text-gray-0" : "text-gray-100"}
                />
            )}
            <span
                className={
                    isActive ? "text-gray-0" : isDisabled ? "text-gray-500" : "text-gray-100"
                }
            >
                {label}
            </span>
        </div>
    )
}

type HoveredSensor = {
    value: SatelliteSensorType
    midY: number
}

// Colors must match the panel's own bg-black-800/border-gray-700.
function InfoPanelArrow({ arrowY }: { arrowY: number }) {
    return (
        <>
            <div
                style={{
                    position: "absolute",
                    right: "100%",
                    top: arrowY,
                    width: 0,
                    height: 0,
                    borderTop: "7px solid transparent",
                    borderBottom: "7px solid transparent",
                    borderRight: "7px solid #28282C",
                }}
            />
            <div
                style={{
                    position: "absolute",
                    right: "calc(100% - 1px)",
                    top: arrowY + 1,
                    width: 0,
                    height: 0,
                    borderTop: "6px solid transparent",
                    borderBottom: "6px solid transparent",
                    borderRight: "6px solid #0C0C0F",
                }}
            />
        </>
    )
}

function SensorInfoPanel({ hovered, rowRight }: { hovered: HoveredSensor; rowRight: number }) {
    const info = sensorInfo[hovered.value]
    const panelW = 220
    const panelH = 260
    const gap = 24
    const left = rowRight + gap
    const topMax = window.innerHeight - 65 - panelH
    const top = Math.max(8, Math.min(topMax, hovered.midY - panelH / 2))
    const arrowY = Math.max(10, Math.min(panelH - 22, hovered.midY - top - 6))

    return (
        <div
            className="pointer-events-none fixed bg-black-800 border border-gray-700 rounded-xl shadow-card"
            style={{ left, top, width: panelW, zIndex: zIndex.PortalledTooltip }}
        >
            <InfoPanelArrow arrowY={arrowY} />
            <div className="flex flex-col p-4 gap-2">
                <small className="text-gray-500 uppercase tracking-[1.2px]">{info.label}</small>
                <div className="text-gray-100 leading-relaxed" style={{ fontSize: 13 }}>
                    {info.description}
                </div>
                <img
                    src={info.image}
                    alt={`${info.label} preview`}
                    className="w-48 h-32 object-cover rounded-md"
                />
                <div className="flex flex-wrap gap-1">
                    <Tag>
                        <span className="text-xs">{info.resolution}</span>
                    </Tag>
                    <Tag>
                        <span className="text-xs">{info.revisit}</span>
                    </Tag>
                </div>
                <small className="text-gray-100">{info.attribution}</small>
            </div>
        </div>
    )
}

export const SatellitePluginActionBody = () => {
    const dispatch = useAppDispatch()
    const userId = useAppSelector(selectUserID)
    const { zoom } = useViewport()
    const { organizationSettingDateFormat } = config.use("organizationSettingDateFormat")
    const [today] = useState(() => new Date())
    const [date, setDate] = useState(today)
    const formattedDate = formatDate({
        value: date,
        format: organizationSettingDateFormat as DateFormat,
    })
    const defaultName = `Satellite - ${formattedDate}`
    const [isDateAccordianOpen, setIsDateAccordianOpen] = useState(false)
    const [temporalBias, setTemporalBias] = useState<SatelliteTemporalBias>("closest")
    const temporalBiasLabel =
        temporalBiasOptions.find((v) => v.value === temporalBias)?.label ?? "Before"
    const [isDateDirectionModalOpen, setIsDateDirectionModalOpen] = useState(false)
    const [sensorTypes, setSensorTypes] = useState<SatelliteSensorType[]>([
        SatelliteSensorType.Visual,
    ])
    const [nameInput, setNameInput] = useState("")
    const [nameEdited, setNameEdited] = useState(false)
    // Must not flip key on the same edit that sets nameEdited true, or the remount undoes the edit.
    const nameKeyRef = useRef(defaultName)
    if (!nameEdited) nameKeyRef.current = defaultName
    const regionSelectorRef = useRef<RegionSelectorHandle>(null)
    const sensorRowRef = useRef<HTMLDivElement>(null)
    const [hoveredSensor, setHoveredSensor] = useState<HoveredSensor | null>(null)
    const [processTooltip, setProcessTooltip] = useState<{ top: number; left: number } | null>(null)
    const hasSensorSelected = sensorTypes.length > 0
    const canZoomConfirm = zoom >= MIN_ZOOM_TO_CONFIRM
    const canConfirm = canZoomConfirm && hasSensorSelected
    const isToday = date.toDateString() === today.toDateString()

    useEffect(() => {
        if (isToday && temporalBias === "after") setTemporalBias("before")
    }, [isToday, temporalBias])

    const toggleSensorType = (value: SatelliteSensorType) => {
        setSensorTypes((current) =>
            current.includes(value) ? current.filter((type) => type !== value) : [...current, value]
        )
    }

    const onCancel = () => {
        dispatch(setActiveFeatureId({ feature: undefined }))
        dispatch(setActiveLayerId({ id: undefined }))
        dispatch(setActiveSidePanelModal(undefined))
    }

    const onConfirm = () => {
        if (!userId || !canConfirm || sensorTypes.length === 0) return
        const region = regionSelectorRef.current?.getCorners()
        if (!region) return

        const layerName = nameInput || defaultName

        if (sensorTypes.length > 1) {
            const groupId = randomUUID()
            dispatch(
                createGroup({
                    data: {
                        name: layerName,
                        createdBy: { userId },
                        groupType: GroupType.Layer,
                    },
                    context: {
                        id: groupId,
                        index: createNewLayerFractionalIndex(store.getState().project.data),
                        parentId: undefined,
                        isEmpty: true,
                    },
                })
            )

            // Sorted so Visual is created last (and lands on top), regardless of click order.
            const orderedSensorTypes = [...sensorTypes].sort((a, b) => b - a)
            orderedSensorTypes.forEach((sensorType, orderOffset) => {
                const sensorLabel = sensorTypeOptions.find(
                    (option) => option.value === sensorType
                )?.label
                // server creates the job after the flight
                dispatch(
                    createSatelliteFlight({
                        data: {
                            createdBy: { userId },
                            region,
                            sensorType,
                            temporalAnchor: Math.floor(date.getTime() / 1000),
                            name: sensorLabel ?? layerName,
                            temporalBias: temporalBias,
                            parentId: groupId,
                        },
                        // orderOffset keeps sibling indices distinct even if requests race server-side.
                        context: { temporalBias, orderOffset },
                    })
                )
            })
        } else {
            const sensorType = sensorTypes[0]
            dispatch(
                createSatelliteFlight({
                    data: {
                        createdBy: { userId },
                        region,
                        sensorType,
                        temporalAnchor: Math.floor(date.getTime() / 1000),
                        name: layerName,
                        temporalBias,
                        parentId: undefined,
                    },
                    context: { temporalBias },
                })
            )
        }
        onCancel()
    }

    return (
        <SidePanelPluginActionsNavigator backTitle="Cancel" onBack={onCancel}>
            <RegionSelector ref={regionSelectorRef} />
            <SidePanelPluginActionsRenamer
                key={nameKeyRef.current}
                defaultName={defaultName}
                onRename={(value) => {
                    if (value !== defaultName) setNameEdited(true)
                    setNameInput(value)
                }}
            />
            <div className="flex flex-col">
                <div className="flex flex-col rounded-lg border border-gray-500 gap-1.5">
                    <div
                        className="group flex px-3 py-2.5 justify-between items-center cursor-pointer select-none"
                        onClick={() => setIsDateAccordianOpen(!isDateAccordianOpen)}
                    >
                        <span className="pointer-events-none text-[14px] leading-none text-gray-100 group-hover:text-gray-0">
                            {`${temporalBiasLabel}: ${formatDate({
                                value: date,
                                format: DateFormat.MMMDDYYYY,
                                omitSuffix: true,
                            })}`}
                        </span>
                        <Icons.Icon
                            Icon={isDateAccordianOpen ? Icons.ArrowDropUp : Icons.ArrowDropdown}
                            iconClassName="text-gray-100 group-hover:text-gray-0"
                        />
                    </div>
                    {isDateAccordianOpen && (
                        <div className="flex flex-col px-3 pb-2.5 gap-4">
                            <div>
                                <small className="text-gray-100">Date</small>
                                <div className=" relative mt-2">
                                    <Input
                                        type="date"
                                        className="[&_input::-webkit-calendar-picker-indicator]:hidden [&_input]:pointer-events-none [&_input]:text-transparent [&>div]:cursor-pointer text-gray-0"
                                        value={toDateInputValue(date)}
                                        max={toDateInputValue(today)}
                                        min={toDateInputValue(EARLIEST_SATELLITE_DATE)}
                                        onChange={(e) => {
                                            if (!e.target.value) return
                                            const picked = parseDateInputValue(e.target.value)
                                            const todayStart = startOfDay(today)
                                            const clamped =
                                                picked > todayStart ? todayStart : picked
                                            setDate(
                                                clamped < EARLIEST_SATELLITE_DATE
                                                    ? EARLIEST_SATELLITE_DATE
                                                    : clamped
                                            )
                                        }}
                                        rightItem={<Icons.SmallIcon Icon={Icons.CalendarMonth} />}
                                    />
                                    <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[16px] text-gray-0">
                                        {formatDate({
                                            value: date,
                                            format: organizationSettingDateFormat as DateFormat,
                                        })}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <div className="flex items-center gap-1.5">
                                    <small className="text-gray-100">Date Direction</small>
                                </div>
                                <div className="mt-2">
                                    <Select<SatelliteTemporalBias>
                                        value={temporalBias}
                                        onChange={setTemporalBias}
                                        options={temporalBiasOptions.map(
                                            ({ value, label, Icon }) => {
                                                const isDisabled = value === "after" && isToday
                                                return {
                                                    value,
                                                    disabled: isDisabled,
                                                    label: (
                                                        <span className="flex items-center gap-[11px]">
                                                            <Icons.SmallIcon
                                                                Icon={Icon}
                                                                iconClassName={
                                                                    isDisabled
                                                                        ? "text-gray-400"
                                                                        : "text-gray-100"
                                                                }
                                                            />
                                                            <span
                                                                className={
                                                                    isDisabled
                                                                        ? "text-white-300"
                                                                        : "text-gray-0"
                                                                }
                                                            >
                                                                {label}
                                                            </span>
                                                        </span>
                                                    ),
                                                }
                                            }
                                        )}
                                    />
                                </div>
                            </div>

                            <div
                                className="group flex w-full justify-center cursor-pointer items-center gap-[7px] select-none"
                                onClick={() => setIsDateDirectionModalOpen(true)}
                            >
                                <small className="text-[12px] leading-none font-normal text-gray-400 uppercase group-hover:text-gray-0">
                                    Learn More
                                </small>
                                <Icons.SmallIcon
                                    Icon={Icons.Information}
                                    iconClassName="text-white-400 group-hover:text-gray-0"
                                />
                            </div>

                            {isDateDirectionModalOpen && (
                                <SatelliteDateDirectionModal
                                    closeModal={() => setIsDateDirectionModalOpen(false)}
                                />
                            )}
                        </div>
                    )}
                </div>

                <div className="border-b border-gray-700 mt-3" />

                <div className="mt-3">
                    <small className="text-gray-100">Sensors*</small>
                    <div className="flex flex-col mt-2" ref={sensorRowRef}>
                        {sensorTypeOptions.map(({ value, label }) => {
                            const isChecked = sensorTypes.includes(value)
                            return (
                                <OptionRow
                                    key={value}
                                    control={
                                        <TriCheckbox
                                            state={isChecked ? "checked" : "unchecked"}
                                            size="small"
                                        />
                                    }
                                    label={label}
                                    isActive={isChecked || hoveredSensor?.value === value}
                                    onClick={() => toggleSensorType(value)}
                                    onMouseEnter={(e) => {
                                        const rect = e.currentTarget.getBoundingClientRect()
                                        setHoveredSensor({
                                            value,
                                            midY: rect.top + rect.height / 2,
                                        })
                                    }}
                                    onMouseLeave={() => setHoveredSensor(null)}
                                />
                            )
                        })}
                    </div>
                    {!hasSensorSelected && (
                        <small className="text-red-500 text-[14px] mt-1 block">
                            Select a sensor to process
                        </small>
                    )}
                </div>

                {hoveredSensor && sensorRowRef.current && (
                    <SensorInfoPanel
                        hovered={hoveredSensor}
                        rowRight={sensorRowRef.current.getBoundingClientRect().right}
                    />
                )}

                <div className="border-b border-gray-700 mt-4" />

                <div className="flex gap-2 mt-4">
                    <ButtonTertiary title="Cancel" onClick={onCancel} className="flex-1 !min-w-0" />
                    <ButtonPrimary
                        title="Process"
                        disabled={!canConfirm}
                        onClick={onConfirm}
                        className="flex-1 !min-w-0 border-x border-x-transparent"
                        onMouseEnter={(e) => {
                            if (canZoomConfirm) return
                            const rect = e.currentTarget.getBoundingClientRect()
                            setProcessTooltip({ top: rect.top + rect.height / 2, left: rect.right })
                        }}
                        onMouseLeave={() => setProcessTooltip(null)}
                    />
                </div>

                {processTooltip && (
                    <div
                        className="pointer-events-none fixed bg-black-800 border border-gray-700 rounded-md px-2 py-1 whitespace-nowrap"
                        style={{
                            left: processTooltip.left + 12,
                            top: processTooltip.top,
                            transform: "translateY(-50%)",
                            zIndex: zIndex.PortalledTooltip,
                        }}
                    >
                        <small className="text-gray-0">{ZOOM_IN_HINT_TEXT}</small>
                    </div>
                )}
            </div>
        </SidePanelPluginActionsNavigator>
    )
}
